import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback
} from "react";
import { useNavigate } from "react-router";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";
import { UserStorage, type UserData } from "@certa/common/utils/userStorage";
import {
  useCheckAuthV2,
  useUserPermissionsV2,
  useLoginMutation,
  useOTPLoginMutation,
  useMFALoginMutation,
  useLogoutMutation,
  getUserQueryData,
  type LogoutResponse
} from "@certa/queries/hooks/authV2.hooks";
import { useConfigReduxCompatibility } from "../hooks/useConfigReduxCompatibility";
import { updateSelectedLanguageV2 } from "@certa/queries/services/authV2.services";
import { LANGUAGE_KEY } from "@certa/common/constants/userStorageConstants";
import type { CaptchaService } from "main/src/modules/LoginPage/types";
import { setUserTimezoneService } from "@certa/queries/services/userTimezone.services";
import type { InstanceConfiguration } from "@certa/queries/types/config.types";
import type { PermissionsAPIResponse } from "@certa/queries/types/permissions.types";
import { useSelector } from "react-redux";
import type { ReduxState } from "main/src/modules/common/interfaces";
import { postLogoutActionV2 } from "../utils/logoutActions";
import { isNewAuthFlow } from "../utils/directAuthActions";

export type AuthenticationContextType = {
  user: UserData | null;
  csrf: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (credentials: {
    username: string;
    password: string;
    captchaToken: string | null;
    captchaService: CaptchaService;
  }) => Promise<UserData>;
  loginOTP: (credentials: {
    username: string;
    password: string;
    captchaToken: string | null;
    captchaService: CaptchaService;
  }) => Promise<UserData>;
  loginMFA: (pin: string) => Promise<UserData>;
  logout: () => Promise<LogoutResponse>;
  clearError: () => void;

  config?: InstanceConfiguration;
};

type ExtendedAuthenticationContextType = AuthenticationContextType & {
  /** @default 0 */
  userId: number;
  permissions: {
    permissions: Record<number, PermissionsAPIResponse["objects"][number]>;
  } | null;
  isPermissionsLoading: boolean;
};

const AuthenticationContext = createContext<AuthenticationContextType | null>(
  null
);

export const AuthenticationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configError
  } = useInstanceConfigQuery();

  const isNewAuthFlowEnabled = isNewAuthFlow();

  // Config Redux compatibility (can be removed if not needed)
  useConfigReduxCompatibility(
    isConfigLoading,
    configError,
    isNewAuthFlowEnabled,
    config
  );

  // For showing error message on UI
  const [authError, setAuthError] = useState<string | null>(null);
  const [shouldDisableUserQuery, setShouldDisableUserQuery] = useState(false);

  // Helper function to update user language if needed
  const updateUserLanguageIfNeeded = async (
    user: UserData
  ): Promise<UserData> => {
    // Get preferred language from localStorage (equivalent to Godaam.preferredLanguage)
    const storedLanguage = localStorage.getItem(LANGUAGE_KEY);

    if (storedLanguage && user?.prefered_language !== storedLanguage) {
      try {
        const response = await updateSelectedLanguageV2(storedLanguage);
        if (response?.prefered_language) {
          UserStorage.updateUserPreferredLanguage(response.prefered_language);

          return {
            ...user,
            prefered_language: response.prefered_language
          };
        }
      } catch (error) {
        console.warn("Failed to update user language:", error);
      }
    }

    return user;
  };

  const userQuery = useCheckAuthV2({
    ...(!isNewAuthFlowEnabled && { enabled: false }),
    // Due to same key as old legacy flow, the handlers are getting triggered. Remove check once old flow is removed.
    ...(isNewAuthFlowEnabled && {
      enabled: !shouldDisableUserQuery,
      onSuccess: async userData => {
        if (userData) {
          UserStorage.setUser({
            ...userData,
            csrf: document.cookie,
            sessionId: userData.sessionId
          });
          await setUserTimezoneService.setTimezoneInSession();
          // Permissions will be refetched automatically due to enabled: !!user
          await updateUserLanguageIfNeeded(userData);
        }
        setAuthError(null);
      },
      onError: error => {
        UserStorage.clearUser();
        setShouldDisableUserQuery(true);
        postLogoutActionV2({
          addNextURL: true,
          navigate
        });
        // Do not set error due to auto login flow
        // setAuthError(error.message || "Authentication failed");
      }
    })
  });
  const { data: user, isLoading: isUserLoading } = userQuery;

  const loginMutation = useLoginMutation({
    onSuccess: () => {
      setAuthError(null);
    },
    onError: error => {
      setAuthError(error.message || "Login failed");
    }
  });

  const otpLoginMutation = useOTPLoginMutation({
    onSuccess: () => {
      setAuthError(null);
    },
    onError: error => {
      setAuthError(error.message || "OTP login failed");
    }
  });

  const mfaLoginMutation = useMFALoginMutation({
    onSuccess: () => {
      setAuthError(null);
    },
    onError: error => {
      setAuthError(error.message || "MFA login failed");
    }
  });

  const logoutMutation = useLogoutMutation({
    onSuccess: response => {
      UserStorage.clearUser();
      setAuthError(null);
      setShouldDisableUserQuery(true);
      postLogoutActionV2({
        redirectURL: response?.redirect_url,
        addNextURL: true,
        navigate
      });
    },
    onError: error => {
      UserStorage.clearUser();
      setAuthError(error.message || "Logout failed");
    }
  });

  const { mutateAsync: login } = loginMutation;
  const { mutateAsync: loginOTP } = otpLoginMutation;
  const { mutateAsync: loginMFA } = mfaLoginMutation;
  const { mutateAsync: logoutMutateAsync } = logoutMutation;
  const logout = useCallback(
    () => logoutMutateAsync(undefined),
    [logoutMutateAsync]
  );
  const clearError = useCallback(() => setAuthError(null), []);

  const value = useMemo(
    () => ({
      // User state
      user: (user as UserData) || null,
      isAuthenticated: !!user && !!UserStorage.getCSRFToken(),
      csrf: UserStorage.getCSRFToken(),
      isLoading:
        isUserLoading ||
        isConfigLoading ||
        loginMutation.isLoading ||
        otpLoginMutation.isLoading ||
        mfaLoginMutation.isLoading ||
        logoutMutation.isLoading,
      error: authError || null,

      // Actions
      login,
      loginOTP,
      loginMFA,
      logout,
      clearError,

      config
    }),
    [
      user,
      isUserLoading,
      isConfigLoading,
      authError,
      config,
      loginMutation.isLoading,
      otpLoginMutation.isLoading,
      mfaLoginMutation.isLoading,
      logoutMutation.isLoading,

      login,
      loginOTP,
      loginMFA,
      clearError,
      logout
    ]
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = (): ExtendedAuthenticationContextType => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthenticationProvider");
  }

  const isNewAuthFlowEnabled = isNewAuthFlow();

  // Goes away with legacy auth flow
  const reduxAuth = useSelector((state: ReduxState) => state.authentication);

  // For both flows, get user data from React Query cache (same "checkAuth" key)
  const userQueryData = getUserQueryData();

  // Use appropriate data source based on flow
  const user = isNewAuthFlowEnabled ? context.user : userQueryData || null;
  const isLoading = isNewAuthFlowEnabled
    ? context.isLoading
    : reduxAuth?.loading || false;
  const error = isNewAuthFlowEnabled ? context.error : reduxAuth?.error || null;
  const isAuthenticated = isNewAuthFlowEnabled
    ? context.isAuthenticated
    : !!userQueryData;

  const permissionsQuery = useUserPermissionsV2(user || null);

  return {
    ...context,
    user,
    userId: user?.id ?? 0,
    isLoading,
    error,
    isAuthenticated,
    permissions: permissionsQuery.data?.objects
      ? { permissions: { ...permissionsQuery.data?.objects } }
      : null,
    isPermissionsLoading: permissionsQuery.isLoading
  };
};
